/**
 * API service for Drill Timer operations
 *
 * All drill timer endpoints are now nested under entities for proper account-level filtering:
 * - /entities/{entityId}/drill-timers/
 * - /entities/{entityId}/drill-timer-logs/
 */
import axios from './instance'

/**
 * Check if an error is a network error (offline)
 */
export const isNetworkError = (error) =>
  error.isNetworkError || error.message === 'Network Error' || !navigator.onLine

/**
 * Drill Timer Template API
 */
export const drillTimerApi = {
  // Templates - now require entityId for proper filtering
  getTemplates: async (entityId, params = {}) =>
    axios.get(`/entities/${entityId}/drill-timers/`, { params }),

  getTemplate: async (entityId, id) => axios.get(`/entities/${entityId}/drill-timers/${id}/`),

  createTemplate: async (entityId, template) =>
    axios.post(`/entities/${entityId}/drill-timers/`, template),

  updateTemplate: async (entityId, id, template) =>
    axios.patch(`/entities/${entityId}/drill-timers/${id}/`, template),

  deleteTemplate: async (entityId, id) =>
    axios.delete(`/entities/${entityId}/drill-timers/${id}/`),

  // Logs - now require entityId for proper filtering
  getLogs: async (entityId, params = {}) =>
    axios.get(`/entities/${entityId}/drill-timer-logs/`, { params }),

  getLog: async (entityId, id) => axios.get(`/entities/${entityId}/drill-timer-logs/${id}/`),

  createLog: async (entityId, log) => axios.post(`/entities/${entityId}/drill-timer-logs/`, log),

  updateLog: async (entityId, id, log) =>
    axios.patch(`/entities/${entityId}/drill-timer-logs/${id}/`, log),

  deleteLog: async (entityId, id) => axios.delete(`/entities/${entityId}/drill-timer-logs/${id}/`),

  // Bulk create (for offline sync)
  bulkCreateLogs: async (entityId, logs) =>
    axios.post(`/entities/${entityId}/drill-timer-logs/bulk/`, { logs }),

  // File uploads
  uploadSignature: async (entityId, file, userId) => {
    const formData = new FormData()
    formData.append('signature', file)
    formData.append('user_id', userId)

    return axios.post(`/entities/${entityId}/drill-timer-logs/upload-signature/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  uploadVideo: async (entityId, file, drillName) => {
    const formData = new FormData()
    formData.append('video', file)
    formData.append('drill_name', drillName)

    return axios.post(`/entities/${entityId}/drill-timer-logs/upload-video/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

/**
 * Convert data URL to File object
 */
export const dataURLToFile = async (dataURL, filename) => {
  const response = await fetch(dataURL)
  const blob = await response.blob()
  return new File([blob], filename, { type: 'image/png' })
}

/**
 * Upload signature from base64 data URL
 */
export const uploadSignatureFromDataURL = async (entityId, dataURL, userId) => {
  const file = await dataURLToFile(dataURL, `signature_${userId}.png`)
  return drillTimerApi.uploadSignature(entityId, file, userId)
}
